// The filter bar's state: what Discover, Watch next, and Explorer filter and sort by, and how it reads from and writes
// to URL parameters. Pure and shared by the server (the title filter) and the browser (the filter bar).
//
// URL parameters (defaults are omitted): services=mine|all|any|<ids>, unseen=0|1, type, anime=only|none, moods=funny,scary,
// themes=war,future, styles=animated,slow, genres, score, match, released, similar, people, sort, foryou=0|1. Type and anime are the type filter every page shares. The parameters of old Discover filters the sheet doesn't offer keep
// their old names and apply as legacy filters. The age and content filter: age=12 (the age of a ladder step),
// hide=disturbing,drugs and ok=violence (the person's choices per kind of content, kept as given).
import {
	CONTENT_KINDS,
	type ContentKind,
	type ContentOverrides,
	MAX_AGE,
	type ViewerLadder,
	changedContent,
	showAllContent,
} from "~/domain/age-content"
import { MAX_MOODS, MOOD_KEYS, type MoodKey } from "~/domain/moods"
import {
	MAX_STYLES,
	MAX_THEMES,
	STYLE_KEYS,
	type StyleKey,
	THEME_KEYS,
	type ThemeKey,
	parseKeys,
} from "~/domain/themes-styles"
import {
	type AnimeChoice,
	type TitleFormat,
	titleTypeOf,
	titleTypeParams,
} from "~/domain/title-type"
import { MAX_PICKS, tasteRefsOf } from "~/domain/taste-picker"
import { type TitleKey, parseTitleKey, titleKey } from "~/utils/title-key"

export const TAG_GROUPS = ["moods", "themes", "styles", "genres"] as const
export type TagGroup = (typeof TAG_GROUPS)[number]

/** The filter groups, in the order of the bits of a title's fail mask. */
export const FILTER_NAMES = [
	"services",
	"notSeenYet",
	"type",
	"anime",
	"moods",
	"themes",
	"styles",
	"genres",
	"minScore",
	"minMatch",
	"released",
	"similarTo",
	"people",
	"legacy",
	"loved",
	"disliked",
	"productionCountries",
	"ageLimit",
	"content",
	"years",
	"runtime",
	"sourceScores",
	"scoreGap",
	"wishlist",
	"popularity",
	"budget",
	"shows",
	"tasteGroups",
] as const
export type FilterName = (typeof FILTER_NAMES)[number]

/** Facts count blocks are absent unless requested, and null when facts are unavailable. */
export type FilterOptionCounts = {
	[Name in Exclude<
		FilterName,
		| "popularity"
		| "budget"
		| "shows"
		| "years"
		| "runtime"
		| "productionCountries"
		| "sourceScores"
		| "scoreGap"
	>]: Record<string, number>
} & {
	sourceScores?: Record<string, number> | null
	scoreGap?: Record<string, number> | null
	budget?: Record<string, number> | null
	shows?: Record<string, number> | null
	popularity?: Record<string, number>
	years?: Record<string, number> | null
	runtime?: Record<string, number> | null
	productionCountries?: Record<string, number> | null
}

export const SORT_KEYS = [
	"popular",
	"top",
	"newest",
	"relevance",
	"match",
] as const
/**
 * Relevance is the order of a ranked list (a search); it's offered only while searching. Match is Best match: taste
 * match, highest first. It needs taste; without it the results fall back to Popular, or Relevance while searching.
 */
export type SortKey = (typeof SORT_KEYS)[number]

export const MIN_SCORES = [0, 60, 70, 80] as const
export type MinScore = (typeof MIN_SCORES)[number]

/** The taste match filter's options: a title needs a taste match of at least this. */
export const MIN_MATCHES = [0, 60, 70, 80, 90] as const
export type MinMatch = (typeof MIN_MATCHES)[number]

export const RELEASED = [
	"any",
	"recent",
	"2010s",
	"2000s",
	"before2000",
] as const
export type Released = (typeof RELEASED)[number]

/**
 * Old Discover filters that the sheet doesn't offer and today's Discover SQL applies. They keep working from old URLs,
 * under their old names, and aren't written by new links. Old parameters today's SQL ignores (keywords, age rating,
 * language, suitability, context) stay ignored.
 */
export const LEGACY_PARAMS = [
	"fingerprintConditions",
	"fingerprintPillars",
	"fingerprintPillarMinTier",
	"maxScore",
] as const
export type LegacyParam = (typeof LEGACY_PARAMS)[number]
export type LegacyFilters = Partial<Record<LegacyParam, string>>

export const SOURCE_KEYS = [
	"imdb",
	"rtCritics",
	"rtAudience",
	"metacritic",
	"metacriticUsers",
	// Movies only: a show never passes a Letterboxd minimum.
	"letterboxd",
] as const
export type SourceKey = (typeof SOURCE_KEYS)[number]
export const SCORE_GAPS = ["agree", "critics", "audience"] as const
export type ScoreGap = (typeof SCORE_GAPS)[number]
export interface FilterRange {
	min?: number
	max?: number
}

export interface FilterState {
	/** Inclusive nominal USD millions; unknown amounts and shows fail. */
	budget?: FilterRange
	boxOffice?: FilterRange
	returns?: "hit" | "flop"
	seasons?: FilterRange
	showStatus?: "running" | "ended" | "canceled"
	/** Total episodes × usual episode length, in hours. */
	hours?: FilterRange
	/** Inclusive rating count and catalog Popular rank (1 first). */
	votes?: FilterRange
	popularityRank?: FilterRange
	/** Stable taste-group keys (or legacy size numbers), any of, up to three. */
	tasteGroups?: string[]
	/** Inclusive release years; overrides the released preset. */
	years?: FilterRange
	/** Inclusive minutes: movie duration or usual episode runtime. */
	runtime?: FilterRange
	/** Absent inherits runtime; null explicitly leaves episodes unlimited. */
	episodeRuntime?: FilterRange | null
	/** AND of minimum normalized scores (0–100); a missing score fails. */
	sourceScores?: Partial<Record<SourceKey, number>>
	scoreGap?: ScoreGap
	/** Any streaming offer in the viewing country; supersedes saved and explicit services. */
	anyService?: boolean
	type: TitleFormat
	/** With anime, only anime, or without it. */
	anime: AnimeChoice
	/** False means everywhere, or the explicit `services`. */
	onMyServices: boolean
	/** Explicit services when onMyServices is false; empty means everywhere. */
	services?: number[]
	/** Request-local viewing country for streaming offers; never writes saved settings. */
	streamingCountry?: string
	/** Country whose age ratings apply; defaults to the streaming country, then the viewer. */
	ratingCountry?: string
	notSeenYet: boolean
	seenBefore?: boolean
	onWishlist?: boolean
	outsideUsual?: boolean
	/** Up to MAX_MOODS; a title fits if it belongs to at least one (all picks when listed in allOf). */
	moods: MoodKey[]
	/** Up to MAX_THEMES; a title fits if it has at least one (all picks when listed in allOf). */
	themes: ThemeKey[]
	/** Up to MAX_STYLES; a title fits if it has at least one (all picks when listed in allOf). */
	styles: StyleKey[]
	/** A title fits if it has at least one (all picks when listed in allOf). */
	genres: string[]
	/** Tag groups whose picks must all match; omitted groups match any pick. */
	allOf?: TagGroup[]
	minScore: number
	/** The least taste match; a title without a match fits only 0. It doesn't narrow for a person without taste. */
	minMatch: MinMatch
	released: Released
	/** Titles similar to any of these. */
	similarTo?: TitleKey[]
	/** Titles with any of these people (TMDB person ids) in the cast or crew. */
	people?: number[]
	/** Any of these production countries; ISO alpha-2 codes. Co-productions match each listed country. */
	productionCountries?: string[]
	restOfWorld?: boolean
	/** Temporary high/low rating signals, never saved to the library. */
	loved?: TitleKey[]
	disliked?: TitleKey[]
	legacy?: LegacyFilters
	/**
	 * The age limit: the age of a step of the viewer's rating ladder; undefined is off (0 is a limit). An age that
	 * isn't a step in the viewer's country stands for the highest step at or below it (ladderStepFor).
	 */
	ageLimit?: number
	/**
	 * The person's choices per kind of content, as given; a kind without an entry follows what the age limit sets
	 * (defaultHiddenKinds). An entry may equal what the limit sets: it stays, so it still holds after the limit changes.
	 * The kinds hidden are hiddenKinds(ageLimit, content); the ones shown as changed, changedContent(ageLimit, content).
	 */
	content?: ContentOverrides
}

/** The defaults depend on the viewer: On my services for members with saved services, Not seen yet for members. */
export interface FilterDefaults {
	onMyServices: boolean
	notSeenYet: boolean
}

export const defaultFilterState = (defaults: FilterDefaults): FilterState => ({
	type: "all",
	anime: "any",
	onMyServices: defaults.onMyServices,
	notSeenYet: defaults.notSeenYet,
	moods: [],
	themes: [],
	styles: [],
	genres: [],
	minScore: 0,
	minMatch: 0,
	released: "any",
})

/** Only countries published with the snapshot can override the existing rating country. */
export function ratingCountryFor(
	state: Pick<FilterState, "ratingCountry" | "streamingCountry">,
	viewerCountry: string,
	countries: readonly string[],
): string {
	const chosen = state.ratingCountry?.trim().toUpperCase()
	return chosen && countries.includes(chosen)
		? chosen
		: (state.streamingCountry ?? viewerCountry).toUpperCase()
}

const MAX_LIST = 20
const list = (value: string | null) =>
	(value ?? "")
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean)
		.slice(0, MAX_LIST)
const ids = (value: string | null, max = 3e12) =>
	[...new Set(list(value).map(Number))].filter(
		(id) => Number.isSafeInteger(id) && id > 0 && id < max,
	)
const kinds = (value: string | null) =>
	list(value).filter((kind): kind is ContentKind =>
		(CONTENT_KINDS as readonly string[]).includes(kind),
	)
const oneOf = <T extends string | number>(
	options: readonly T[],
	value: unknown,
): T | undefined => options.find((option) => String(option) === value)

// Empty, negative and reversed ranges are ignored; money and hours accept decimals. New names take precedence.
function rangeFromParams(
	params: URLSearchParams,
	name: string,
	oldMin?: string,
	oldMax?: string,
): FilterRange | undefined {
	const raw = params.has(name)
		? params.get(name)!
		: `${oldMin ? (params.get(oldMin) ?? "") : ""}-${
				oldMax ? (params.get(oldMax) ?? "") : ""
			}`
	const decimal = ["budget", "boxOffice", "hours"].includes(name)
	const match = (
		decimal ? /^(\d*(?:\.\d+)?)-(\d*(?:\.\d+)?)$/ : /^(\d*)-(\d*)$/
	).exec(raw)
	if (!match || (!match[1] && !match[2])) return undefined
	const min = match[1] ? Number(match[1]) : undefined
	const max = match[2] ? Number(match[2]) : undefined
	if (
		[min, max].some(
			(n) =>
				n !== undefined &&
				!(decimal
					? Number.isFinite(n) && n <= Number.MAX_SAFE_INTEGER
					: Number.isSafeInteger(n)),
		) ||
		(min !== undefined && max !== undefined && min > max)
	)
		return undefined
	return {
		...(min === undefined ? {} : { min }),
		...(max === undefined ? {} : { max }),
	}
}

export function filterStateFromParams(
	params: URLSearchParams,
	defaults: FilterDefaults,
	member = true,
): FilterState {
	const state = defaultFilterState(defaults)
	const streamingCountry = params.get("streamingCountry")?.trim().toUpperCase()
	if (streamingCountry && /^[A-Z]{2}$/.test(streamingCountry))
		state.streamingCountry = streamingCountry
	const ratingCountry = params.get("ratingCountry")?.trim().toUpperCase()
	if (ratingCountry && /^[A-Z]{2}$/.test(ratingCountry))
		state.ratingCountry = ratingCountry
	const services = params.get("services")
	if (services === "mine") state.onMyServices = true
	else if (services === "any") {
		state.anyService = true
		state.onMyServices = false
	} else if (services === "all") state.onMyServices = false
	else if (services) {
		const chosen = ids(services, 1e9)
		state.onMyServices = false
		if (chosen.length) state.services = chosen
	}
	// Last enabled seen/unseen choice wins, including repeats. A zero disables only its own choice.
	// Guests ignore seen entirely, so shared links cannot turn off their Not seen yet default.
	for (const [name, value] of params) {
		if (name === "unseen" && (value === "0" || value === "1")) {
			state.notSeenYet = value === "1"
			if (state.notSeenYet) delete state.seenBefore
		} else if (member && name === "seen" && (value === "0" || value === "1")) {
			if (value === "1") {
				state.seenBefore = true
				state.notSeenYet = false
			} else delete state.seenBefore
		}
	}
	if (params.get("wishlist") === "1") state.onWishlist = true
	const tasteGroups = tasteRefsOf(params.get("taste")).slice(0, MAX_PICKS)
	if (tasteGroups.length) state.tasteGroups = tasteGroups
	const titleType = titleTypeOf(params.get("type"), params.get("anime"))
	state.type = titleType.format
	state.anime = titleType.anime
	state.moods = [
		...new Set(
			list(params.get("moods")).filter((key): key is MoodKey =>
				(MOOD_KEYS as readonly string[]).includes(key),
			),
		),
	].slice(0, MAX_MOODS)
	state.themes = parseKeys(THEME_KEYS, params.get("themes"), MAX_THEMES)
	state.styles = parseKeys(STYLE_KEYS, params.get("styles"), MAX_STYLES)
	state.genres = [...new Set(list(params.get("genres")))]
	const all = list(params.get("all"))
	const allOf = TAG_GROUPS.filter(
		(group) => all.includes(group) && state[group].length >= 2,
	)
	if (allOf.length) state.allOf = allOf
	const score = params.get("score")
	state.minScore =
		score !== null && /^\d{1,3}$/.test(score) && Number(score) <= 100
			? Number(score)
			: 0
	state.minMatch = oneOf(MIN_MATCHES, params.get("match")) ?? 0
	if (params.get("match") === "outside") state.outsideUsual = true
	for (const field of ["budget", "boxOffice", "seasons", "hours"] as const) {
		const range = rangeFromParams(params, field)
		if (range) state[field] = range
	}
	const returns = oneOf(["hit", "flop"] as const, params.get("returns"))
	if (returns) state.returns = returns
	const showStatus = oneOf(
		["running", "ended", "canceled"] as const,
		params.get("showStatus"),
	)
	if (showStatus) state.showStatus = showStatus
	const votes = rangeFromParams(params, "votes")
	if (votes) state.votes = votes
	const rank = rangeFromParams(params, "rank")
	if (rank) state.popularityRank = rank
	const years = rangeFromParams(params, "years", "minYear", "maxYear")
	if (years) state.years = years
	const runtime = rangeFromParams(params, "runtime", "minRuntime", "maxRuntime")
	if (runtime) state.runtime = runtime
	if (params.get("episodeRuntime") === "any") state.episodeRuntime = null
	else {
		const episodeRuntime = rangeFromParams(params, "episodeRuntime")
		if (episodeRuntime) state.episodeRuntime = episodeRuntime
	}
	if (params.get("restOfWorld") === "1") state.restOfWorld = true
	state.released = state.years
		? "any"
		: (oneOf(RELEASED, params.get("released")) ?? "any")
	const sources: Partial<Record<SourceKey, number>> = {}
	for (const part of list(params.get("sources"))) {
		const [key, value] = part.split(":")
		const source = oneOf(SOURCE_KEYS, key)
		if (source && /^\d{1,3}$/.test(value ?? "") && Number(value) <= 100)
			sources[source] = Number(value)
	}
	if (Object.keys(sources).length) state.sourceScores = sources
	const gap = oneOf(SCORE_GAPS, params.get("gap"))
	if (gap) state.scoreGap = gap
	for (const name of ["loved", "disliked"] as const) {
		const chosen = [
			...new Set(
				(params.get(name) ?? "").split(",").map((value) => {
					const match = /^(movie|show):(\d+)$/.exec(value.trim())
					if (match) {
						const id = Number(match[2])
						return Number.isSafeInteger(id) && id > 0 && id < 1e12
							? titleKey(match[1] === "movie" ? "movie" : "show", id)
							: NaN
					}
					// Existing shared URLs used the internal numeric title key.
					return Number(value)
				}),
			),
		].filter((key) => Number.isSafeInteger(key) && key > 0 && key < 3e12)
		if (chosen.length) state[name] = chosen
	}
	// Disliked wins if a hand-written URL names a title in both groups.
	if (state.loved)
		state.loved = state.loved.filter((key) => !state.disliked?.includes(key))
	const productionCountries = [
		...new Set(
			(params.get("productionCountries") ?? "")
				.split(",")
				.map((code) => code.trim().toUpperCase()),
		),
	].filter((code) => /^[A-Z]{2}$/.test(code))
	if (productionCountries.length)
		state.productionCountries = productionCountries
	const similar = ids(params.get("similar"))
	if (similar.length) state.similarTo = similar
	const people = ids(params.get("people"), 1e9)
	if (people.length) state.people = people
	const legacy: LegacyFilters = {}
	for (const name of LEGACY_PARAMS) {
		const value = params.get(name)?.trim()
		if (value) legacy[name] = value
	}
	if (Object.keys(legacy).length) state.legacy = legacy
	const age = params.get("age")
	if (age !== null && /^\d{1,2}$/.test(age) && Number(age) <= MAX_AGE)
		state.ageLimit = Number(age)
	// A kind named in both is hidden.
	const overrides: ContentOverrides = {}
	for (const kind of kinds(params.get("ok"))) overrides[kind] = "show"
	for (const kind of kinds(params.get("hide"))) overrides[kind] = "hide"
	if (Object.keys(overrides).length) state.content = overrides
	return state
}

export function filterStateToParams(
	state: FilterState,
	defaults: FilterDefaults,
	params = new URLSearchParams(),
): URLSearchParams {
	const set = (name: string, value: string | null) => {
		if (value === null) params.delete(name)
		else params.set(name, value)
	}
	set("seen", state.seenBefore ? "1" : null)
	set("wishlist", state.onWishlist ? "1" : null)
	set(
		"taste",
		tasteRefsOf(state.tasteGroups?.join(",")).slice(0, MAX_PICKS).join(",") ||
			null,
	)
	set("streamingCountry", state.streamingCountry ?? null)
	set("ratingCountry", state.ratingCountry ?? null)
	const services = state.anyService
		? "any"
		: state.onMyServices
			? "mine"
			: state.services?.length
				? state.services.join(",")
				: "all"
	set(
		"services",
		services === (defaults.onMyServices ? "mine" : "all") ? null : services,
	)
	set(
		"unseen",
		state.notSeenYet === defaults.notSeenYet
			? null
			: state.notSeenYet
				? "1"
				: "0",
	)
	const titleType = titleTypeParams({ format: state.type, anime: state.anime })
	set("type", titleType.type)
	set("anime", titleType.anime)
	set("moods", state.moods.length ? state.moods.join(",") : null)
	set("themes", state.themes.length ? state.themes.join(",") : null)
	set("styles", state.styles.length ? state.styles.join(",") : null)
	set("genres", state.genres.length ? state.genres.join(",") : null)
	const allOf = TAG_GROUPS.filter(
		(group) => state.allOf?.includes(group) && new Set(state[group]).size >= 2,
	)
	set("all", allOf.length ? allOf.join(",") : null)
	set("score", state.minScore ? String(state.minScore) : null)
	set(
		"match",
		state.outsideUsual
			? "outside"
			: state.minMatch
				? String(state.minMatch)
				: null,
	)
	set(
		"released",
		state.years || state.released === "any" ? null : state.released,
	)
	for (const name of [
		"years",
		"runtime",
		"episodeRuntime",
		"votes",
		"popularityRank",
		"budget",
		"boxOffice",
		"seasons",
		"hours",
	] as const) {
		const range = state[name]
		set(
			name === "popularityRank" ? "rank" : name,
			range === null
				? state.runtime &&
					(state.runtime.min !== undefined || state.runtime.max !== undefined)
					? "any"
					: null
				: range && (range.min !== undefined || range.max !== undefined)
					? `${range.min ?? ""}-${range.max ?? ""}`
					: null,
		)
	}
	set("returns", state.returns ?? null)
	set("showStatus", state.showStatus ?? null)
	set("restOfWorld", state.restOfWorld ? "1" : null)
	for (const name of ["minYear", "maxYear", "minRuntime", "maxRuntime"])
		params.delete(name)
	set(
		"sources",
		SOURCE_KEYS.filter((key) => state.sourceScores?.[key] !== undefined)
			.map((key) => `${key}:${state.sourceScores![key]}`)
			.join(",") || null,
	)
	set("gap", state.scoreGap ?? null)
	set("similar", state.similarTo?.length ? state.similarTo.join(",") : null)
	for (const name of ["loved", "disliked"] as const)
		set(
			name,
			state[name]?.length
				? state[name]
						.map((key) => {
							if (key < 1e12) return String(key)
							const { mediaType, tmdbId } = parseTitleKey(key)
							return `${mediaType}:${tmdbId}`
						})
						.join(",")
				: null,
		)
	set(
		"productionCountries",
		state.productionCountries?.length
			? state.productionCountries.join(",")
			: null,
	)
	set("people", state.people?.length ? state.people.join(",") : null)
	for (const name of LEGACY_PARAMS) set(name, state.legacy?.[name] ?? null)
	set("age", state.ageLimit === undefined ? null : String(state.ageLimit))
	// Every override is written, also one the age limit sets anyway: it has to be there after the limit changes.
	const chosen = (choice: "hide" | "show") =>
		CONTENT_KINDS.filter((kind) => state.content?.[kind] === choice).join(
			",",
		) || null
	set("hide", chosen("hide"))
	set("ok", chosen("show"))
	return params
}

/**
 * The filter and sort parameters alone, for fetching counts and results: Discover's loader and the filter bar build
 * the same string for the same state. On my services and Not seen yet are spelled out, so the server's defaults for
 * the viewer can't read them differently.
 */
export function filterQuery(
	state: FilterState,
	sort: SortKey,
	defaults: FilterDefaults,
): string {
	const out = filterStateToParams(state, defaults)
	if (!out.has("services"))
		out.set("services", state.onMyServices ? "mine" : "all")
	out.set("unseen", state.notSeenYet ? "1" : "0")
	out.set("sort", sort)
	return out.toString()
}

/** The sort from `sort`; Relevance only while searching, where it's also the default. Best match is kept as asked. */
export function sortFromParams(
	params: URLSearchParams,
	searching: boolean,
): SortKey {
	const sort = oneOf(SORT_KEYS, params.get("sort"))
	if (sort === "relevance") return searching ? sort : "popular"
	return sort ?? (searching ? "relevance" : "popular")
}

/** Resolve newly enabled, mutually exclusive choices after a state change. */
export function resolveFilterChange(
	previous: FilterState,
	next: FilterState,
): FilterState {
	if (next.seenBefore && !previous.seenBefore)
		next = { ...next, notSeenYet: false }
	else if (next.notSeenYet && !previous.notSeenYet)
		next = { ...next, seenBefore: undefined }
	if (next.outsideUsual && !previous.outsideUsual)
		next = { ...next, minMatch: 0 }
	else if (next.minMatch !== previous.minMatch)
		next = { ...next, outsideUsual: undefined }
	return next
}

/** The state with one filter group back at its widest: what a one-tap recovery does. */
export function dropFilter(state: FilterState, name: FilterName): FilterState {
	switch (name) {
		case "budget":
			return {
				...state,
				budget: undefined,
				boxOffice: undefined,
				returns: undefined,
			}
		case "shows":
			return {
				...state,
				seasons: undefined,
				showStatus: undefined,
				hours: undefined,
			}
		case "popularity":
			return { ...state, votes: undefined, popularityRank: undefined }
		case "runtime":
			return { ...state, runtime: undefined, episodeRuntime: undefined }
		case "tasteGroups":
		case "years":
		case "sourceScores":
		case "scoreGap":
			return { ...state, [name]: undefined }
		case "services":
			return {
				...state,
				onMyServices: false,
				anyService: undefined,
				services: undefined,
				streamingCountry: undefined,
			}
		case "notSeenYet":
			return { ...state, notSeenYet: false, seenBefore: undefined }
		case "wishlist":
			return { ...state, onWishlist: undefined }
		case "type":
			return { ...state, type: "all" }
		case "anime":
			return { ...state, anime: "any" }
		case "moods":
			return { ...state, moods: [] }
		case "themes":
			return { ...state, themes: [] }
		case "styles":
			return { ...state, styles: [] }
		case "genres":
			return { ...state, genres: [] }
		case "minScore":
			return { ...state, minScore: 0 }
		case "minMatch":
			return { ...state, minMatch: 0, outsideUsual: undefined }
		case "released":
			return { ...state, released: "any" }
		case "similarTo":
			return { ...state, similarTo: undefined }
		case "loved":
		case "disliked":
			return { ...state, [name]: undefined }
		case "productionCountries":
			return {
				...state,
				productionCountries: undefined,
				restOfWorld: undefined,
			}
		case "people":
			return { ...state, people: undefined }
		case "legacy":
			return { ...state, legacy: undefined }
		// The content overrides stay, so the limit switched on again finds them.
		case "ageLimit":
			return { ...state, ageLimit: undefined }
		// Every kind shown, whatever the age limit sets.
		case "content":
			return { ...state, content: showAllContent(state.ageLimit) }
	}
}

/** The Filters sheet's filters cleared; On my services and Not seen yet stay as they are. */
export const clearSecondaryFilters = (state: FilterState): FilterState => ({
	...state,
	seenBefore: undefined,
	onWishlist: undefined,
	tasteGroups: undefined,
	outsideUsual: undefined,
	allOf: undefined,
	budget: undefined,
	boxOffice: undefined,
	returns: undefined,
	seasons: undefined,
	showStatus: undefined,
	hours: undefined,
	votes: undefined,
	popularityRank: undefined,
	years: undefined,
	runtime: undefined,
	episodeRuntime: undefined,
	restOfWorld: undefined,
	sourceScores: undefined,
	scoreGap: undefined,
	anyService: undefined,
	type: "all",
	anime: "any",
	services: undefined,
	streamingCountry: undefined,
	ratingCountry: undefined,
	moods: [],
	themes: [],
	styles: [],
	genres: [],
	minScore: 0,
	minMatch: 0,
	released: "any",
	loved: undefined,
	disliked: undefined,
	similarTo: undefined,
	productionCountries: undefined,
	people: undefined,
	legacy: undefined,
	ageLimit: undefined,
	content: undefined,
})

/** How many of the Filters sheet's filters are active: the badge on the Filters button. */
export function secondaryFilterCount(state: FilterState): number {
	return (
		(state.streamingCountry || state.anyService ? 1 : 0) +
		(state.ratingCountry ? 1 : 0) +
		[
			state.budget,
			state.boxOffice,
			state.returns,
			state.seasons,
			state.showStatus,
			state.hours,
		].filter(Boolean).length +
		(state.votes ? 1 : 0) +
		(state.popularityRank ? 1 : 0) +
		(state.years ? 1 : 0) +
		(state.runtime || state.episodeRuntime ? 1 : 0) +
		Object.keys(state.sourceScores ?? {}).length +
		(state.scoreGap ? 1 : 0) +
		(state.type !== "all" ? 1 : 0) +
		(state.anime !== "any" ? 1 : 0) +
		(!state.onMyServices && state.services?.length ? 1 : 0) +
		state.moods.length +
		state.themes.length +
		state.styles.length +
		state.genres.length +
		(state.minScore ? 1 : 0) +
		(state.minMatch || state.outsideUsual ? 1 : 0) +
		(state.seenBefore ? 1 : 0) +
		(state.onWishlist ? 1 : 0) +
		(state.tasteGroups?.length ?? 0) +
		(state.released !== "any" ? 1 : 0) +
		(state.similarTo?.length ?? 0) +
		(state.people?.length ?? 0) +
		(state.productionCountries?.length ?? 0) +
		(state.restOfWorld ? 1 : 0) +
		(state.loved?.length ?? 0) +
		(state.disliked?.length ?? 0) +
		Object.keys(state.legacy ?? {}).length +
		(state.ageLimit === undefined ? 0 : 1) +
		Object.keys(changedContent(state.ageLimit, state.content) ?? {}).length
	)
}

/**
 * The state as it applies, for everything that shows a filter as active (chips, the count on Filters, Clear
 * filters). The age limit and content apply only with the viewer's ladder: without one (the age filter is off for the
 * viewer, or the snapshot has no ratings; also while the first counts are on their way) the title filter ignores
 * them, so they must not look applied. Seen before, Wishlist and Outside your usual apply only to members.
 * Budget and show facts choices apply unless the snapshot explicitly reports facts unavailable.
 * The URL keeps them.
 */
export const stateAsApplied = (
	state: FilterState,
	ladder: ViewerLadder | null | undefined,
	member: boolean,
	viewerCountry?: string,
	facts?: boolean,
): FilterState => {
	let applied = state
	if (facts === false)
		applied = {
			...applied,
			budget: undefined,
			boxOffice: undefined,
			returns: undefined,
			seasons: undefined,
			showStatus: undefined,
			hours: undefined,
		}
	if (
		state.ratingCountry &&
		(!ladder ||
			state.ratingCountry !== ladder.country ||
			state.ratingCountry === (state.streamingCountry ?? viewerCountry))
	)
		applied = { ...applied, ratingCountry: undefined }
	if (!ladder && (state.ageLimit !== undefined || state.content))
		applied = { ...applied, ageLimit: undefined, content: undefined }
	if (
		!member &&
		(state.seenBefore ||
			state.onWishlist ||
			state.outsideUsual ||
			state.tasteGroups?.length)
	)
		applied = {
			...applied,
			seenBefore: undefined,
			onWishlist: undefined,
			outsideUsual: undefined,
			tasteGroups: undefined,
		}
	return applied
}

// Today's Discover parameters that have a new name. The legacy filters keep theirs.
const RENAMED_DISCOVER_PARAMS = [
	"withGenres",
	"withStreamingProviders",
	"streamingPreset",
	"minScore",
	"sortBy",
	"sortDirection",
	"watchedType",
	"similarTitles",
	"withCast",
	"withCrew",
	"withCastCombinationType",
	"withCrewCombinationType",
	"withStreamingTypes",
] as const
const OLD_SORTS: Record<string, SortKey> = {
	popularity: "popular",
	aggregated_score: "top",
	release_date: "newest",
}

const DISCOVER_PATH_TYPES: Record<string, "movie" | "show"> = {
	movies: "movie",
	movie: "movie",
	shows: "show",
	show: "show",
}

/** The `type` a Discover type path stands for (/discover/movies is /discover?type=movie); undefined for others. */
export const typeOfDiscoverPath = (
	segment: string,
): "movie" | "show" | undefined =>
	Object.prototype.hasOwnProperty.call(DISCOVER_PATH_TYPES, segment)
		? DISCOVER_PATH_TYPES[segment]
		: undefined

/**
 * Today's Discover parameters rewritten to the new names, or null when the URL carries none of them, so a caller
 * redirects only old links. Genres were ids and are names now, so the caller passes the genre table.
 * - withGenres (ids) becomes genres (names); withStreamingProviders becomes services; streamingPreset mine or
 *   everywhere becomes services=mine or all; minScore becomes score, the highest option at or below it; sortBy becomes
 *   sort; watchedType didnt-watch becomes unseen=1; similarTitles (tmdbId:mediaType) becomes similar (title keys);
 *   withCast and withCrew become people; type=movies becomes type=movie.
 * - Old parameters that only shaped the old controls (sortDirection, the cast and crew combination types,
 *   withStreamingTypes, and watchedType watched or want-to-watch) are dropped. Everything else passes through.
 */
export function rewriteLegacyDiscoverParams(
	params: URLSearchParams,
	genreNameById: (id: number) => string | undefined,
): URLSearchParams | null {
	const oldType = params.get("type")
	const typeIsOld = oldType === "movies" || oldType === "all"
	if (!typeIsOld && !RENAMED_DISCOVER_PARAMS.some((name) => params.has(name)))
		return null

	const next = new URLSearchParams(params)
	for (const name of RENAMED_DISCOVER_PARAMS) next.delete(name)
	const numbers = (value: string | null) =>
		list(value)
			.map(Number)
			.filter((n) => Number.isSafeInteger(n) && n > 0)
	// A new parameter already in the URL wins over the old one.
	const setIfMissing = (name: string, values: (string | number)[]) => {
		const value = [...new Set(values)].join(",")
		if (value && !next.get(name)) next.set(name, value)
	}

	if (oldType === "movies") next.set("type", "movie")
	else if (oldType === "all") next.delete("type")

	setIfMissing(
		"genres",
		numbers(params.get("withGenres"))
			.map(genreNameById)
			.filter((name): name is string => Boolean(name)),
	)

	const providers = numbers(params.get("withStreamingProviders"))
	const preset = params.get("streamingPreset")
	if (providers.length) setIfMissing("services", providers)
	else if (preset === "mine") setIfMissing("services", ["mine"])
	else if (preset === "everywhere") setIfMissing("services", ["all"])

	const minScore = Number(params.get("minScore"))
	const score = [...MIN_SCORES].reverse().find((option) => option <= minScore)
	if (score) setIfMissing("score", [score])

	const sort = OLD_SORTS[params.get("sortBy") ?? ""]
	if (sort && sort !== "popular") setIfMissing("sort", [sort])

	if (params.get("watchedType") === "didnt-watch") setIfMissing("unseen", ["1"])

	setIfMissing(
		"similar",
		list(params.get("similarTitles")).flatMap((item) => {
			const [id, mediaType] = item.split(":")
			const tmdbId = Number(id)
			if (!Number.isSafeInteger(tmdbId) || tmdbId <= 0) return []
			if (mediaType !== "movie" && mediaType !== "show") return []
			return [titleKey(mediaType, tmdbId)]
		}),
	)

	setIfMissing("people", [
		...numbers(params.get("withCast")),
		...numbers(params.get("withCrew")),
	])

	return next
}
